import { createStyles, makeStyles } from "@material-ui/core";
import { Autocomplete, AutocompleteProps } from "@material-ui/lab";
import classnames from "classnames";
import * as React from "react";

import { RelodeTheme, TextInput } from "..";
import { baseColors } from "../styles";
import {
    UnusedAriaProps,
    UnusedMuiEvents,
    UnusedMuiStandardProps,
} from "../types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "inline-flex",
                verticalAlign: "top",
            },
            chip: {
                fontSize: "16px",
                lineHeight: "24px",
                marginBottom: theme.spacing(1),
                marginRight: theme.spacing(1),
                backgroundColor: theme.relode.colors.chip.selected.light,
                color: theme.relode.colors.chip.selected.contrastText,
                "&:hover": {
                    backgroundColor: theme.relode.colors.chip.selected.main,
                },
            },
            popupIndicator: {
                color: baseColors.inactive_gray,
                "&:hover": {
                    backgroundColor: theme.relode.colors.chip.unselected.light,
                },
            },
            clearIndicator: {
                color: baseColors.inactive_gray,
                "&:hover": {
                    backgroundColor: theme.relode.colors.chip.unselected.light,
                },
            },
        }),
    { name: "SelectInput" }
);

// NOTE:  need more intentional way of differentiating how the SelectInput component is used so we require a mode prop.
// suggest = API provided list that may not match input text so disable filterOptions
// fixed = known list that we want to filter and match against the input text so use default filterOptions behavior
export type SelectInputMode = "suggest" | "list";

export type SelectInputProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined
> = {
    label: string;
    clearable?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    disableTyping?: boolean;
    disableFullWidth?: boolean;
    disableWrapContent?: boolean;
    mode: SelectInputMode;
    maxCharacters?: number;
    "data-cy"?: string;
} & Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    | "renderInput"
    | "disableClearable"
    | "fullWidth"
    | UnusedAriaProps
    | UnusedMuiEvents
    | UnusedMuiStandardProps
> &
    Pick<
        AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
        "onChange" | "placeholder" | "defaultValue" | "className" | "onBlur"
    >;

export const SelectInput = <
    T,
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>(
    props: SelectInputProps<T, Multiple, DisableClearable, FreeSolo>
) => {
    const {
        autoSelect = true,
        label,
        error,
        helperText,
        clearable,
        placeholder,
        onInputChange,
        disableTyping,
        disableFullWidth,
        disableWrapContent,
        mode,
        options = [],
        onBlur,
        maxCharacters,
        "data-cy": dataCy,
        ...passthrough
    } = props;

    const classes = useStyles(props);

    return (
        <Autocomplete
            {...passthrough}
            autoSelect={autoSelect}
            fullWidth={!disableFullWidth}
            className={classnames(props.className, classes.root)}
            selectOnFocus={!disableTyping}
            disableClearable={!clearable}
            onInputChange={onInputChange}
            options={options}
            filterOptions={mode === "suggest" ? (o, s) => o : undefined}
            ChipProps={{
                className: classes.chip,
            }}
            openText=""
            closeText=""
            clearText=""
            classes={{
                popupIndicator: classes.popupIndicator,
                clearIndicator: classes.clearIndicator,
            }}
            onBlur={onBlur}
            renderInput={params => (
                <TextInput
                    {...params}
                    inputProps={{
                        ...params.inputProps,
                        readOnly: !!disableTyping ? "readonly" : undefined,
                        maxLength: maxCharacters,
                    }}
                    error={error}
                    placeholder={placeholder}
                    helperText={helperText}
                    label={label}
                    disableFullWidth={disableFullWidth}
                    wrapContent={!disableWrapContent}
                    ref={params.InputProps.ref}
                    data-cy={dataCy}
                    compress
                />
            )}
        />
    );
};
